import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";

export default function ReviewsSection() {
  const { t } = useLang();
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  if (!reviews || reviews.length === 0) return null;

  const displayName = (name) => {
    const parts = name.trim().split(" ");
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
  };

  return (
    <section className="bg-white border-y border-terracotta/10" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 sm:py-28">
        <Reveal className="text-center mb-12">
          <Caption>{t("reviews.caption")}</Caption>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-4 text-ink">{t("reviews.title")}</h2>
          <p className="text-ink/55 text-sm mt-4">{t("reviews.subtitle")}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((r, i) => (
            <Reveal key={r.id} delay={(i % 3) * 0.08}>
              <figure data-testid={`review-card-${r.id}`} className="bg-sand rounded-3xl border border-terracotta/10 p-7 h-full flex flex-col">
                {r.photo && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photo}`}
                    alt={`${displayName(r.name)}'s stay`}
                    data-testid={`review-photo-${r.id}`}
                    className="w-full h-44 object-cover rounded-2xl mb-5"
                    loading="lazy"
                  />
                )}
                <div className="flex gap-1 text-gold">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={16} fill={s < r.rating ? "#E8A838" : "none"} strokeWidth={s < r.rating ? 0 : 1.5} className={s < r.rating ? "" : "text-ink/20"} />
                  ))}
                </div>
                <blockquote className="text-sm text-ink/70 leading-relaxed mt-4">“{r.text}”</blockquote>
                {r.owner_reply && (
                  <div className="mt-4 pl-4 border-l-2 border-terracotta/30 grow-0" data-testid={`review-reply-${r.id}`}>
                    <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-terracotta mb-1">{t("reviews.hostReply")}</p>
                    <p className="text-xs text-ink/60 leading-relaxed italic">{r.owner_reply}</p>
                  </div>
                )}
                <div className="grow" />
                <figcaption className="mt-5 text-xs font-mono uppercase tracking-[0.15em] text-ink/45">
                  {displayName(r.name)} · {fmtDate(r.created_at)}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
